import { useEffect, useState } from "react";
import { getRegionApiUrl } from "../config/backend";
import { chaosParticipationService } from "../services/chaosParticipationService";
import { startVisibleInterval } from "../utils/visibleInterval";

/**
 * TRAC-550 B3: Polls /health/dependencies in BOTH regions every 15s (while the tab is visible,
 * TRACI-849) so the
 * pin-flip state machine (B4) can decide whether the user's currently-pinned
 * region has died and the other one is still serving.
 *
 * State semantics:
 *   - `healthy`   : 2xx response from /health/dependencies (TRAC-549 endpoint)
 *   - `unhealthy` : non-2xx response, OR the request couldn't complete (network
 *                   error / timeout / abort / DNS failure). The headline failure
 *                   mode for this feature is a region the user can't reach at
 *                   all — counting those into the flip threshold is what makes
 *                   the failover work. WiFi-blip noise is filtered by the
 *                   freshness guard at the failover-service layer (a target
 *                   region must have a recent reachable response to be flipped
 *                   to), not by ignoring unreachable polls here.
 *
 * `consecutive` counts unhealthy/unreachable responses in a row. A subsequent
 * healthy response resets it; otherwise it increments. `lastSeen` is only
 * refreshed when the request actually completed — that's how the freshness
 * guard at the failover layer can tell "we got a real answer" from "we got
 * silence."
 */

export type RegionHealthState = "healthy" | "unhealthy";

export interface RegionStatus {
  state: RegionHealthState;
  /** Timestamp of the last response that actually completed, null until first. */
  lastSeen: Date | null;
  /** Consecutive non-healthy responses (non-2xx OR unreachable). */
  consecutive: number;
}

export interface RegionsHealth {
  usEast1: RegionStatus;
  usWest2: RegionStatus;
}

const POLL_INTERVAL_MS = 15_000;
const FETCH_TIMEOUT_MS = 5_000;

const INITIAL_STATUS: RegionStatus = {
  state: "healthy",
  lastSeen: null,
  consecutive: 0,
};

interface ProbeResult {
  ok: boolean;
  reachable: boolean;
}

async function probeRegion(region: "us-east-1" | "us-west-2"): Promise<ProbeResult> {
  const url = getRegionApiUrl(region);
  const ctrl = new AbortController();
  const timeoutId = window.setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(`${url}/health/dependencies`, {
      cache: "no-store",
      credentials: "omit",
      signal: ctrl.signal,
      // TRAC-548: when this session has opted into a chaos drill via the
      // Dev Settings toggle, inject the drill header so the backend's
      // chaos middleware can scope its 503s to participating sessions.
      // Empty object when no drill is active or session not participating.
      headers: chaosParticipationService.getDrillHeader(),
    });
    return { ok: res.ok, reachable: true };
  } catch {
    return { ok: false, reachable: false };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export function applyResponse(prev: RegionStatus, result: ProbeResult): RegionStatus {
  if (result.reachable && result.ok) {
    return { state: "healthy", lastSeen: new Date(), consecutive: 0 };
  }
  return {
    state: "unhealthy",
    lastSeen: result.reachable ? new Date() : prev.lastSeen,
    consecutive: prev.consecutive + 1,
  };
}

export function useRegionHealth(): RegionsHealth {
  const [usEast1, setUsEast1] = useState<RegionStatus>(INITIAL_STATUS);
  const [usWest2, setUsWest2] = useState<RegionStatus>(INITIAL_STATUS);

  useEffect(() => {
    let cancelled = false;

    const probeBoth = async () => {
      const [eastResult, westResult] = await Promise.all([
        probeRegion("us-east-1"),
        probeRegion("us-west-2"),
      ]);
      if (cancelled) return;
      setUsEast1((prev) => applyResponse(prev, eastResult));
      setUsWest2((prev) => applyResponse(prev, westResult));
    };

    void probeBoth();
    // TRACI-849: a hidden tab stops probing and probes once on return. The failover only flips
    // to a region with a fresh healthy probe, so a stale snapshot after a hidden spell can delay
    // a flip but never cause one.
    const stop = startVisibleInterval(() => void probeBoth(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      stop();
    };
  }, []);

  return { usEast1, usWest2 };
}
